import { Button, Col, Divider, Drawer, Form, Image, Input, InputNumber, List, Row, Select, Spin, Switch, TreeSelect, Typography, Upload, message } from "antd";
import { useState, useEffect } from "react";
// import { useSelector } from "react-redux";
import { productAPIWithErrorHandling } from "../../../../api/product/product";
import { PlusOutlined, PlusCircleOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { transformData } from "../../../../utils/recursive";
// import { store } from "../../../../redux/configStore";
import useStoreData from "../../../../hooks/useStoreData";
import { resolveImageUrl } from "../../../../utils/image";
import { brand } from "../../../../theme/tokens";

const { Text } = Typography;

const sectionTitleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: brand.accent,
  margin: 0,
};

export default function ProductForm({ open, onCancel, onSuccess, initialData, categories, branchId }) {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedType, setSelectedType] = useState("1");
  const [comboItems, setComboItems] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();

  const storeOptions = transformData(allListStore?.data)?.filter(
    (item) => item.parent_id === 0
  );

  const isCombo = selectedType === "9";

  // Lấy danh sách sản phẩm để chọn cho combo
  const fetchProducts = async () => {
    try {
      const requests = [productAPIWithErrorHandling.getList({ per_page: 200, typeId: 1 })];

      const [res] = await Promise.all(requests);
      if (res.success) {
        setAllProducts(res.data.data || []);
      }
    } catch (error) {
      console.error("Lỗi lấy dữ liệu:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProducts();
      if (initialData) {
        // Chuyển đổi status sang số
        const normalizedData = {
          ...initialData,
          status: initialData.status === "active" || initialData.status == 1 ? 1 : 0,
          typeId: String(initialData.typeId || "1"),
        };
        form.setFieldsValue(normalizedData);
        setSelectedType(normalizedData.typeId);

        // Xử lý preview ảnh
        if (initialData.image) {
          setImagePreview(resolveImageUrl(initialData.image));
        } else {
          setImagePreview(null);
        }

        // Xử lý combos nếu có
        if (initialData.combos && typeof initialData.combos === 'object') {
          const items = Object.keys(initialData.combos).map(id => ({
            id: id,
            ...initialData.combos[id]
          }));
          setComboItems(items);
        } else {
          setComboItems([]);
        }
      } else {
        form.resetFields();
        setSelectedType("1");
        setComboItems([]);
        setImagePreview(null);

        // Nếu có branchId truyền từ ngoài, set mặc định
        if (branchId) {
          form.setFieldsValue({ mcoupon_branch_id: branchId });
        }
      }
    }
  }, [open, initialData, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);

      const formData = new FormData();

      // Append primitive fields
      Object.entries(values).forEach(([key, val]) => {
        if (key !== 'image' && key !== 'combos' && key !== 'features' && val !== undefined && val !== null) {
          formData.append(key, val);
        }
      });

      // Đặc điểm (mảng) -> JSON string
      formData.append("features", JSON.stringify(Array.isArray(values.features) ? values.features : []));

      // Xử lý image
      if (values.image && values.image.file) {
        formData.append("image", values.image.file);
      }

      // Xử lý combos
      if (isCombo) {
        const combosObj = {};
        comboItems.forEach(item => {
          combosObj[item.id] = {
            quantity: item.quantity || 1,
            code: item.code,
            name: item.name
          };
        });
        formData.append("combos", JSON.stringify(combosObj));
      }

      let res;
      if (initialData) {
        res = await productAPIWithErrorHandling.update(initialData.id, formData);
      } else {
        res = await productAPIWithErrorHandling.create(formData);
      }

      if (res.success) {
        message.success(initialData ? "Cập nhật sản phẩm thành công" : "Thêm sản phẩm mới thành công");
        onSuccess(res.data);
      }
    } catch (error) {
      // Lỗi validate: cuộn tới field đầu tiên bị lỗi (form dài, lỗi dễ nằm ngoài vùng nhìn)
      if (error?.errorFields?.length) {
        form.scrollToField(error.errorFields[0].name, { behavior: "smooth", block: "center" });
        return;
      }
      if (error.message) {
        message.error(error.message);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleImageChange = (info) => {
    if (info.file) {
      const url = URL.createObjectURL(info.file);
      setImagePreview(url);
    }
  };

  const addComboItem = (productId) => {
    const product = allProducts.find(p => p.id === productId);
    if (product && !comboItems.find(item => item.id === productId)) {
      setComboItems([...comboItems, {
        id: product.id,
        name: product.name,
        code: product.code,
        quantity: 1
      }]);
    }
  };

  const removeComboItem = (id) => {
    setComboItems(comboItems.filter(item => item.id !== id));
  };

  const updateComboQuantity = (id, quantity) => {
    setComboItems(comboItems.map(item =>
      item.id === id ? { ...item, quantity } : item
    ));
  };

  return (
    <Drawer
      title={
        <span style={{ fontSize: 18, fontWeight: 700 }}>
          {initialData ? "Chỉnh sửa sản phẩm" : "Thêm mới sản phẩm"}
        </span>
      }
      open={open}
      onClose={onCancel}
      width={isCombo ? 1180 : 880}
      destroyOnClose
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "4px 0",
          }}
        >
          <Button onClick={onCancel} style={{ minWidth: 80 }}>
            Hủy
          </Button>
          <Button
            type="primary"
            icon={initialData ? null : <PlusCircleOutlined />}
            onClick={handleOk}
            loading={confirmLoading}
            style={{ minWidth: 120 }}
          >
            {initialData ? "Lưu thay đổi" : "Thêm mới"}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="product_form"
        requiredMark="optional"
        style={{ marginTop: 8 }}
        initialValues={{
          status: 1,
          price: 0,
          remain_stock: 0,
          typeId: "1",
          typeName: "Sản phẩm",
          show_on_miniapp: false
        }}
      >
        <Row gutter={24}>
          <Col xs={24} sm={isCombo ? 12 : 24} span={isCombo ? 12 : 24}>
            <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0, marginBottom: 16 }}>
              <Text style={sectionTitleStyle}>Thông tin cơ bản</Text>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={12} span={12}>
                <Form.Item name="image" label="Hình ảnh sản phẩm">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleImageChange({ file });
                      return false;
                    }}
                  >
                    <div className="flex items-center gap-4 border p-2 rounded bg-gray-50 hover:border-blue-400 cursor-pointer transition-all">
                      {imagePreview ? (
                        <Image src={imagePreview} width={60} height={60} className="object-cover rounded" preview={false} />
                      ) : (
                        <div className="w-[60px] h-[60px] bg-gray-200 rounded flex items-center justify-center">
                          <UploadOutlined className="text-gray-400 text-xl" />
                        </div>
                      )}
                      <Button icon={<PlusOutlined />} size="small">Chọn ảnh</Button>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} span={12}>
                <Form.Item
                  name="name"
                  label="Tên sản phẩm"
                  rules={[{ required: true, message: "Vui lòng nhập tên sản phẩm" }]}
                >
                  <Input placeholder="Nhập tên sản phẩm" />
                </Form.Item>
                <Form.Item
                  name="code"
                  label="Mã sản phẩm"
                >
                  <Input placeholder="Tự sinh nếu để trống" />
                </Form.Item>
                <Form.Item
                  name="mcoupon_branch_id"
                  label="Chi nhánh quản lý"
                  rules={[{ required: true, message: "Vui lòng chọn chi nhánh!" }]}
                >
                  {isLoadStoreAll ? (
                    <Spin size="small" />
                  ) : (
                    <TreeSelect
                      allowClear
                      treeData={storeOptions}
                      placeholder="Chọn chi nhánh áp dụng"
                      treeDefaultExpandAll
                      disabled={!!branchId}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 16 }}>
              <Text style={sectionTitleStyle}>Phân loại & giá</Text>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={12} span={12}>
                <Form.Item
                  name="service_category_id"
                  label="Danh mục"
                >
                  <Select placeholder="Chọn danh mục" allowClear>
                    {categories.map(c => (
                      <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} span={12}>
                <Form.Item
                  name="typeId"
                  label="Loại sản phẩm"
                  required
                >
                  <Select
                    onChange={(val) => {
                      setSelectedType(val);
                      form.setFieldValue("typeName", val === "9" ? "Combo" : "Sản phẩm");
                    }}
                  >
                    <Select.Option value="1">Sản phẩm</Select.Option>
                    <Select.Option value="9">Combo</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="typeName" hidden><Input /></Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12} span={12}>
                <Form.Item
                  name="price"
                  label="Giá bán (đ)"
                  rules={[{ type: 'number', min: 0 }]}
                >
                  <InputNumber className="w-full" formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} span={12}>
                <Form.Item
                  name="remain_stock"
                  label="Số lượng tồn ban đầu"
                  rules={[{ type: 'number', min: 0 }]}
                  extra={
                    isCombo ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Combo tính tồn theo sản phẩm thành phần
                      </Text>
                    ) : null
                  }
                >
                  <InputNumber className="w-full" disabled={isCombo} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 16 }}>
              <Text style={sectionTitleStyle}>Trưng bày trên Miniapp</Text>
            </Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="show_on_miniapp"
                  label="Hiển thị trên Miniapp"
                  valuePropName="checked"
                  tooltip="Bật để sản phẩm xuất hiện trong danh sách trưng bày trên Miniapp. Series hiển thị theo Danh mục đã chọn."
                >
                  <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="description" label="Mô tả ngắn">
                  <Input.TextArea rows={2} maxLength={500} showCount placeholder="Mô tả hiển thị trên Miniapp" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} span={8}>
                <Form.Item name="thickness" label="Độ dày">
                  <Input placeholder="VD: 75mm" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} span={8}>
                <Form.Item name="size" label="Kích thước">
                  <Input placeholder="VD: 1150 × 2400 mm" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} span={8}>
                <Form.Item name="unit" label="Đơn vị">
                  <Input placeholder="VD: tấm, m²" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="features"
                  label="Đặc điểm nổi bật"
                  tooltip="Gõ từng đặc điểm rồi Enter để thêm"
                >
                  <Select
                    mode="tags"
                    open={false}
                    tokenSeparators={[",", "\n"]}
                    placeholder="VD: Cách nhiệt tốt, Chống ẩm, Bảo hành 10 năm..."
                  />
                </Form.Item>
              </Col>
            </Row>

          </Col>

          {isCombo && (
            <Col xs={24} sm={12} span={12}>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <Text strong>Thành phần Combo</Text>
                <Divider style={{ margin: '12px 0' }} />

                <Select
                  showSearch
                  placeholder="Chọn sản phẩm con..."
                  style={{ width: '100%', marginBottom: '16px' }}
                  onChange={addComboItem}
                  value={null}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={allProducts.map(p => ({
                    label: `${p.name} (${p.code})`,
                    value: p.id
                  }))}
                />

                <List
                  dataSource={comboItems}
                  renderItem={item => (
                    <List.Item
                      actions={[
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeComboItem(item.id)}
                        />
                      ]}
                    >
                      <List.Item.Meta
                        title={<span className="text-sm">{item.name}</span>}
                        description={<span className="text-xs">{item.code}</span>}
                      />
                      <InputNumber
                        min={1}
                        size="small"
                        value={item.quantity}
                        onChange={(val) => updateComboQuantity(item.id, val)}
                        style={{ width: 60 }}
                      />
                    </List.Item>
                  )}
                  locale={{ emptyText: 'Chưa có sản phẩm nào trong combo' }}
                  style={{ maxHeight: 300, overflow: 'auto' }}
                />
              </div>
            </Col>
          )}
        </Row>
      </Form>
    </Drawer>
  );
}
